import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import './Dashboard.css';

const LABOUR_CATEGORIES = ['Construction','Interior','Plumbing','Wiring','Painting','Carpentry','Masonry','Tiling','Welding','Other'];
const MATERIAL_CATEGORIES = ['Cement','Steel','Bricks','Sand','Tiles','Paints','Wood','Electrical','Plumbing','Hardware','Other'];

interface Project {
  id: number;
  name: string;
  location: string;
  status: string;
  startDate: string;
  budget: string;
}

interface VendorProfile {
  id: number;
  name: string;
  phone: string;
  email?: string;
  company?: string;
  category: string;
  address?: string;
  rate?: string;
  notes?: string;
  quotation?: any;
  dailyStatuses?: any[];
  type?: 'labour' | 'material';
  projects?: Array<{ id: number; name: string; status: string }>;
  projectsCount?: number;
}

interface SubscriptionInfo {
  hasSubscription: boolean;
  canStartTrial: boolean;
  isExpired?: boolean;
  subscription?: {
    planName: string;
    status: string;
    daysRemaining: number;
    endDate: string;
  };
}

const Dashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [vendorProfiles, setVendorProfiles] = useState<VendorProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [subscriptionInfo, setSubscriptionInfo] = useState<SubscriptionInfo | null>(null);
  const [vendorTab, setVendorTab] = useState<'labour' | 'material'>('labour');
  const [catFilter, setCatFilter] = useState('All');

  useEffect(() => {
    if (user?.role === 'client') {
      fetchClientProjects();
    } else if (user?.role === 'vendor') {
      fetchVendorProfile();
    }
    // Fetch subscription info for architects
    if (user?.role === 'architect') {
      fetchSubscriptionInfo();
    }
  }, [user]);

  const fetchSubscriptionInfo = async () => {
    try {
      const response = await api.get('/subscriptions/current');
      setSubscriptionInfo(response.data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  };

  const fetchClientProjects = async () => {
    try {
      setLoading(true);
      const response = await api.get('/projects/my-projects');
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVendorProfile = async () => {
    try {
      setLoading(true);
      const profiles: VendorProfile[] = [];

      // Fetch all projects first
      let allProjects: any[] = [];
      try {
        const projectsResponse = await api.get('/projects');
        allProjects = projectsResponse.data;
        console.log('All projects fetched:', allProjects);
      } catch (err) {
        console.error('Error fetching projects:', err);
      }

      // Fetch labour vendor profiles by phone
      try {
        const response = await api.get('/labour-vendors/my-profile');
        console.log('Labour vendor response:', response.data);
        const labourProfiles = Array.isArray(response.data) ? response.data : [response.data];
        labourProfiles.forEach(profile => {
          // Parse notes field to extract project names (one per line)
          let projectsFromNotes: Array<{ id: number; name: string; status: string }> = [];
          if (profile.notes && profile.notes.trim()) {
            const projectNames = profile.notes.split('\n').filter((name: string) => name.trim());
            projectsFromNotes = projectNames.map((name: string, index: number) => ({
              id: index,
              name: name.trim(),
              status: 'Active'
            }));
          }

          profiles.push({
            ...profile,
            type: 'labour',
            projects: projectsFromNotes,
            projectsCount: projectsFromNotes.length
          });
        });
      } catch (err: any) {
        console.error('Labour vendor error:', err.response?.data || err.message);
      }

      // Fetch material vendor profiles by phone
      try {
        const response = await api.get('/material-vendors/my-profile');
        console.log('Material vendor response:', response.data);
        const materialProfiles = Array.isArray(response.data) ? response.data : [response.data];
        materialProfiles.forEach(profile => {
          // Parse notes field to extract project names (one per line)
          let projectsFromNotes: Array<{ id: number; name: string; status: string }> = [];
          if (profile.notes && profile.notes.trim()) {
            const projectNames = profile.notes.split('\n').filter((name: string) => name.trim());
            projectsFromNotes = projectNames.map((name: string, index: number) => ({
              id: index,
              name: name.trim(),
              status: 'Active'
            }));
          }

          profiles.push({
            ...profile,
            type: 'material',
            projects: projectsFromNotes,
            projectsCount: projectsFromNotes.length
          });
        });
      } catch (err: any) {
        console.error('Material vendor error:', err.response?.data || err.message);
      }

      console.log('Final vendor profiles with projects:', profiles);
      // Auto-select the correct tab based on what profiles exist
      const hasLabourInResult = profiles.some(p => p.type === 'labour');
      const hasMaterialInResult = profiles.some(p => p.type === 'material');
      if (!hasLabourInResult && hasMaterialInResult) {
        setVendorTab('material');
      } else {
        setVendorTab('labour');
      }
      setVendorProfiles(profiles);
    } catch (error) {
      console.error('Error fetching vendor profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Architect Dashboard - only show Client, Labour Vendor, Material Vendor buttons
  if (user?.role === 'architect') {
    return (
      <div className="dashboard-container">
        <header className="dashboard-header">
          <div className="header-logo">
            <img src="/logo.png" alt="Arc Track" className="logo-icon-small" />
          </div>
          <button onClick={handleLogout} className="btn-logout">
            Logout
          </button>
        </header>

        <div className="dashboard-content">
          <div className="welcome-card">
            <h2>Welcome, {user?.name}!</h2>
            <p className="welcome-subtitle">Architect Dashboard</p>
          </div>

          {/* Subscription Status Banner */}
          {subscriptionInfo && (
            <div
              className="subscription-banner"
              style={{
                background: subscriptionInfo.hasSubscription && !subscriptionInfo.isExpired
                  ? (subscriptionInfo.subscription?.daysRemaining && subscriptionInfo.subscription.daysRemaining <= 3
                    ? 'linear-gradient(135deg, #ff9800, #f57c00)'
                    : 'linear-gradient(135deg, #4CAF50, #45a049)')
                  : subscriptionInfo.canStartTrial
                    ? 'linear-gradient(135deg, #2196F3, #1976D2)'
                    : 'linear-gradient(135deg, #f44336, #d32f2f)',
                color: 'white',
                padding: '15px 20px',
                borderRadius: '12px',
                marginBottom: '25px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '10px'
              }}
            >
              <div>
                {subscriptionInfo.hasSubscription && !subscriptionInfo.isExpired ? (
                  <>
                    <div style={{ fontWeight: '600', fontSize: '16px' }}>
                      {subscriptionInfo.subscription?.planName}
                    </div>
                    <div style={{ fontSize: '14px', opacity: 0.9 }}>
                      {subscriptionInfo.subscription?.daysRemaining && subscriptionInfo.subscription.daysRemaining <= 3
                        ? `Expires in ${subscriptionInfo.subscription.daysRemaining} day(s)!`
                        : `${subscriptionInfo.subscription?.daysRemaining} days remaining`}
                    </div>
                  </>
                ) : subscriptionInfo.canStartTrial ? (
                  <>
                    <div style={{ fontWeight: '600', fontSize: '16px' }}>
                      Start Your Free Trial!
                    </div>
                    <div style={{ fontSize: '14px', opacity: 0.9 }}>
                      Try Arc Track free for 7 days
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ fontWeight: '600', fontSize: '16px' }}>
                      Subscription Expired
                    </div>
                    <div style={{ fontSize: '14px', opacity: 0.9 }}>
                      Renew to continue using all features
                    </div>
                  </>
                )}
              </div>
              <button
                onClick={() => navigate('/subscription')}
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: '2px solid white',
                  color: 'white',
                  padding: '10px 20px',
                  borderRadius: '25px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  fontSize: '14px',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = 'white';
                  e.currentTarget.style.color = '#333';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                  e.currentTarget.style.color = 'white';
                }}
              >
                {subscriptionInfo.hasSubscription && !subscriptionInfo.isExpired
                  ? 'Manage Subscription'
                  : subscriptionInfo.canStartTrial
                    ? 'Start Free Trial'
                    : 'Renew Now'}
              </button>
            </div>
          )}

          <div className="architect-menu">
            <button className="menu-btn menu-btn-client" onClick={() => navigate('/clients')}>
              <div className="menu-btn-icon">👤</div>
              <h3>Client</h3>
              <p>Manage building clients</p>
            </button>

            <button className="menu-btn menu-btn-labour" onClick={() => navigate('/labour-vendors')}>
              <div className="menu-btn-icon">👷</div>
              <h3>Labour Vendor</h3>
              <p>Manage labour vendors</p>
            </button>

            <button className="menu-btn menu-btn-material" onClick={() => navigate('/material-vendors')}>
              <div className="menu-btn-icon">🏗️</div>
              <h3>Material Vendor</h3>
              <p>Manage material vendors</p>
            </button>

            <button className="menu-btn menu-btn-enquiry" onClick={() => navigate('/enquiries')}>
              <div className="menu-btn-icon">📩</div>
              <h3>Enquiries</h3>
              <p>View website enquiries by date</p>
            </button>

            <button className="menu-btn menu-btn-partners" onClick={() => navigate('/partners')}>
              <div className="menu-btn-icon">📋</div>
              <h3>Partner Directory</h3>
              <p>External labour, material, architect & builder partners</p>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Client Dashboard
  if (user?.role === 'client') {
    return (
      <div className="dash-page">
        <header className="dash-header">
          <img src="/logo.png" alt="Arc Track" className="logo-icon-small" />
          <h1 className="dash-title">My Projects</h1>
          <button type="button" onClick={handleLogout} className="dash-logout">Logout</button>
        </header>

        <div className="dash-body">
          {loading && <div className="dash-loading">Loading projects...</div>}

          {!loading && projects.length === 0 && (
            <div className="dash-empty">
              <p>No projects assigned yet</p>
              <small>Your architect will add projects for you</small>
            </div>
          )}

          {!loading && projects.length > 0 && (
            <div className="dash-cards">
              {projects.map((project) => (
                <div
                  key={project.id}
                  className="dash-card"
                  onClick={() => navigate(`/client/${user.id}/project/${project.id}`)}
                >
                  <div className="dash-card-avatar dash-avatar-client">
                    {project.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="dash-card-body">
                    <h3 className="dash-card-name">{project.name}</h3>
                    <p className="dash-card-sub">📍 {project.location}</p>
                    {project.budget && <p className="dash-card-sub">💰 Budget: {project.budget}</p>}
                    <span className={`dash-badge dash-status-${project.status.toLowerCase().replace(' ', '-')}`}>
                      {project.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Vendor Dashboard
  if (user?.role === 'vendor') {
    const labourProfiles = vendorProfiles.filter(p => p.type === 'labour');
    const materialProfiles = vendorProfiles.filter(p => p.type === 'material');
    const hasLabour = labourProfiles.length > 0;
    const hasMaterial = materialProfiles.length > 0;
    const activeProfiles = vendorTab === 'labour' ? labourProfiles : materialProfiles;
    const filteredProfiles = catFilter === 'All' ? activeProfiles : activeProfiles.filter(p => p.category === catFilter);
    const allCats = vendorTab === 'labour' ? LABOUR_CATEGORIES : MATERIAL_CATEGORIES;
    const title = vendorTab === 'labour' ? 'Labour Work' : 'Material Supply';

    const handleCategoryClick = async (cat: string) => {
      if (cat === 'All') { setCatFilter('All'); return; }
      // Find existing profile for this category
      const existing = activeProfiles.find(p => p.category === cat);
      if (existing) {
        navigate(`/${vendorTab}-vendor/${existing.id}`);
        return;
      }
      // Auto-create profile for this category, then navigate
      try {
        const endpoint = vendorTab === 'labour' ? '/labour-vendors/my-profile/ensure' : '/material-vendors/my-profile/ensure';
        const res = await api.post(endpoint, { category: cat });
        navigate(`/${vendorTab}-vendor/${res.data.id}`);
      } catch (err: any) {
        alert(err.response?.data?.message || 'Failed to open work page');
      }
    };

    return (
      <div className="clients-container">
        <header className="clients-header">
          <div className="header-left">
            {hasLabour && hasMaterial ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <button
                  className="btn-back"
                  style={{ opacity: vendorTab === 'labour' ? 1 : 0.5 }}
                  onClick={() => { setVendorTab('labour'); setCatFilter('All'); }}
                >
                  👷 Labour
                </button>
                <button
                  className="btn-back"
                  style={{ opacity: vendorTab === 'material' ? 1 : 0.5 }}
                  onClick={() => { setVendorTab('material'); setCatFilter('All'); }}
                >
                  🏗️ Material
                </button>
              </div>
            ) : (
              <div />
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
            <img src="/logo.png" alt="Arc Track" className="logo-icon-small" />
            <h1>{title}</h1>
          </div>
          <button className="btn-add-client" onClick={handleLogout}>Logout</button>
        </header>

        {/* Category filter tabs */}
        <div style={{ padding: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          {['All', ...allCats].map(cat => {
            const count = cat === 'All' ? activeProfiles.length : activeProfiles.filter(p => p.category === cat).length;
            const active = catFilter === cat;
            return (
              <button
                type="button"
                key={cat}
                onClick={() => handleCategoryClick(cat)}
                style={{
                  padding: '8px 16px', borderRadius: '20px', border: 'none', cursor: 'pointer',
                  fontWeight: '600', fontSize: '14px',
                  background: active ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#e0e0e0',
                  color: active ? 'white' : '#333'
                }}
              >
                {cat} ({count})
              </button>
            );
          })}
        </div>

        <div className="clients-content">
          {loading && <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>Loading your profiles...</div>}

          {!loading && vendorProfiles.length === 0 && (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#666' }}>
              <h3>No profile found</h3>
              <p>Your architect needs to add you as a vendor first.</p>
            </div>
          )}

          {!loading && filteredProfiles.length > 0 && (
            <div className="clients-grid">
              {filteredProfiles.map((profile) => (
                <div
                  key={`${profile.type}-${profile.id}`}
                  className="client-card"
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/${profile.type}-vendor/${profile.id}`)}
                >
                  <div
                    className="client-avatar"
                    style={{
                      background: profile.type === 'labour'
                        ? 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
                        : 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'
                    }}
                  >
                    {profile.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="client-info">
                    <h3>{profile.name}</h3>
                    {profile.company && <p style={{ fontSize: '13px', color: '#888', marginBottom: '8px' }}>{profile.company}</p>}
                    <p className="client-phone">📱 {profile.phone}</p>
                    <div style={{ marginTop: '8px' }}>
                      <span style={{
                        display: 'inline-block', padding: '4px 12px',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white', borderRadius: '12px', fontSize: '12px', fontWeight: '600'
                      }}>
                        {profile.category}
                      </span>
                    </div>
                    {profile.rate && (
                      <p style={{ fontSize: '13px', color: '#333', marginTop: '8px', fontWeight: '600' }}>
                        Rate: {profile.rate}
                      </p>
                    )}
                    {profile.projects && profile.projects.length > 0 && (
                      <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #e0e0e0' }}>
                        <p style={{ fontSize: '12px', fontWeight: '600', color: '#666', marginBottom: '5px' }}>Projects:</p>
                        {profile.projects.map((proj) => (
                          <div key={proj.id} style={{ fontSize: '13px', color: '#333', marginBottom: '3px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <span>📋</span><span>{proj.name}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <div style={{ marginTop: '12px' }}>
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/${profile.type}-vendor/${profile.id}`); }}
                        style={{
                          width: '100%', padding: '8px',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: 'white', border: 'none', borderRadius: '6px',
                          cursor: 'pointer', fontWeight: '600', fontSize: '13px'
                        }}
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Admin Dashboard
  if (user?.role === 'admin') {
    return (
      <div className="dashboard-container">
        <header className="dashboard-header">
          <div className="header-logo">
            <img src="/logo.png" alt="Arcivi" className="logo-icon-small" />
          </div>
          <button type="button" onClick={handleLogout} className="btn-logout">Logout</button>
        </header>
        <div className="dashboard-content">
          <div className="welcome-card">
            <h2>Admin Panel</h2>
            <p>Welcome, {user?.name}</p>
          </div>
          <div className="nav-grid">
            <button type="button" className="nav-card" onClick={() => navigate('/admin/users')}>
              <div className="nav-icon">👥</div>
              <h3>User Management</h3>
              <p>Approve, manage & control user access</p>
            </button>
            <button type="button" className="nav-card" onClick={() => navigate('/admin/portfolio')}>
              <div className="nav-icon">🖼️</div>
              <h3>Portfolio</h3>
              <p>Manage portfolio projects & images</p>
            </button>
            <button type="button" className="nav-card" onClick={() => navigate('/enquiries')}>
              <div className="nav-icon">📩</div>
              <h3>Enquiries</h3>
              <p>View website enquiries by date</p>
            </button>
            <button type="button" className="nav-card" onClick={() => navigate('/partners')}>
              <div className="nav-icon">📋</div>
              <h3>Partner Directory</h3>
              <p>External labour, material, architect & builder partners</p>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Default fallback
  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <img src="/logo.png" alt="Arc Track" className="logo-icon-small" />
        <button onClick={handleLogout} className="btn-logout">
          Logout
        </button>
      </header>
      <div className="dashboard-content">
        <div className="welcome-card">
          <h2>Welcome, {user?.name}!</h2>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
